import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { RecommendationSource } from "@/lib/enums";
import { recommendationSourceKeys } from "@/lib/query";

export interface RecommendationSourceSetting {
  id: string;
  source: RecommendationSource;
  createdAt: string;
  updatedAt: string;
}

export interface RecommendationSourceInput {
  source?: RecommendationSource;
}

// Singleton — GET + PUT only, no create/delete hooks (mirrors
// use-allowance-period.ts / src/api/recommendation-source.ts).
export function useRecommendationSource() {
  return useQuery({
    queryKey: recommendationSourceKeys.detail(),
    queryFn: async (): Promise<RecommendationSourceSetting> => {
      const res = await fetch("/api/recommendation-source");
      if (!res.ok) throw new Error("Failed to fetch recommendation source");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useUpdateRecommendationSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updates: RecommendationSourceInput,
    ): Promise<RecommendationSourceSetting> => {
      const res = await fetch("/api/recommendation-source", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Update failed");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recommendationSourceKeys.all });
      toast.success("Recommendation source updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update recommendation source");
    },
  });
}
